// Dates and times on patient pages, in the page's language.
//
// Intl.DateTimeFormat has no Haitian Creole: Chromium, Safari and Node ship no CLDR data for `ht`,
// so `toLocaleDateString("ht", …)` quietly prints English. A locale listed here is formatted on a
// base locale's skeleton (one with the same day-month order and punctuation) with its own weekday
// and month names swapped in; its times come from `time`. A browser that does know the locale
// (`Intl.DateTimeFormat.supportedLocalesOf`) uses its own data instead.
//
// Adding a language: run `Intl.DateTimeFormat.supportedLocalesOf(["xx"])` in Node; when it comes
// back empty, add the names and the conjunction here from config/locales/xx.yml (features/translation/ADDING_A_LANGUAGE.md).
import { toTimezone } from "~/util/date";

type FallbackNames = {
  base: string;
  time: string;
  // The conjunction a list ends with ("1, 2 and 4"); Intl.ListFormat has no data either.
  and: string;
  months: string[];
  monthsShort: string[];
  days: string[];
  daysShort: string[];
};

export const INTL_FALLBACKS: Record<string, FallbackNames> = {
  ht: {
    base: "fr-HT",
    time: "en-US",
    and: "ak",
    months: [
      "janvye",
      "fevriye",
      "mas",
      "avril",
      "me",
      "jen",
      "jiyè",
      "out",
      "septanm",
      "oktòb",
      "novanm",
      "desanm",
    ],
    monthsShort: [
      "jan",
      "fev",
      "mas",
      "avr",
      "me",
      "jen",
      "jiy",
      "out",
      "sep",
      "okt",
      "nov",
      "des",
    ],
    days: ["dimanch", "lendi", "madi", "mèkredi", "jedi", "vandredi", "samdi"],
    daysShort: ["dim", "len", "mad", "mèk", "jed", "van", "sam"],
  },
};

const fallbacks = new Map<string, FallbackNames | undefined>();

function fallbackFor(locale: string): FallbackNames | undefined {
  if (!fallbacks.has(locale)) {
    const known = Intl.DateTimeFormat.supportedLocalesOf([locale]).length > 0;
    fallbacks.set(locale, known ? undefined : INTL_FALLBACKS[locale.split("-")[0]!]);
  }
  return fallbacks.get(locale);
}

// `date.toLocaleDateString(locale, options)`, with the fallback above when Intl lacks the locale.
export function localeDate(
  locale: string,
  date: Date,
  options: Intl.DateTimeFormatOptions,
): string {
  const fallback = fallbackFor(locale);
  if (!fallback) return date.toLocaleDateString(locale, options);

  // The weekday and month the date falls on in the page's time zone.
  const local = options.timeZone ? toTimezone(date, options.timeZone) : date;
  const dayIndex = local.getDay();
  const monthIndex = local.getMonth();

  return new Intl.DateTimeFormat(fallback.base, options)
    .formatToParts(date)
    .map((part) => {
      if (part.type === "weekday") {
        return (
          (options.weekday === "long" ? fallback.days : fallback.daysShort)[dayIndex] ?? part.value
        );
      }
      // Only a month the base locale printed as a name is swapped; a numeric one stays numeric.
      if (part.type === "month" && !/^\d+$/.test(part.value)) {
        return (
          (options.month === "long" ? fallback.months : fallback.monthsShort)[monthIndex] ??
          part.value
        );
      }
      return part.value;
    })
    .join("");
}

// `date.toLocaleTimeString(locale, options)`, with the fallback's clock when Intl lacks the locale.
export function localeTime(
  locale: string,
  date: Date,
  options: Intl.DateTimeFormatOptions,
): string {
  const fallback = fallbackFor(locale);
  return date.toLocaleTimeString(fallback ? fallback.time : locale, options);
}

// `new Intl.ListFormat(locale, {type: "conjunction"}).format(items)`, with the fallback's own
// conjunction when Intl lacks the locale.
export function localeList(locale: string, items: string[]): string {
  if (Intl.ListFormat.supportedLocalesOf([locale]).length > 0) {
    return new Intl.ListFormat(locale, { type: "conjunction" }).format(items);
  }
  const and = INTL_FALLBACKS[locale.split("-")[0]!]?.and ?? "and";
  if (items.length < 2) return items.join("");
  return `${items.slice(0, -1).join(", ")} ${and} ${items.at(-1)}`;
}
